import { UnitSettings } from '../core/types';

/**
 * Project Template Specifications
 *
 * Declarative definitions of the built-in templates, mirroring BIM's
 * template architecture: a template is not an empty sheet but a pre-seeded
 * document carrying levels, views, phases, units and title blocks that a
 * new project (or a new template) is derived from.
 *
 * Built-in templates are instantiated through the BIM kernel at creation
 * time, so they always stay in sync with the kernel's document model.
 */

export interface TemplateLevelSpec {
    name: string;
    /** Elevation in millimeters above the project base point. */
    elevation: number;
    isBuildingStory: boolean;
}

export interface ProjectTemplateSpec {
    id: string;
    name: string;
    description: string;
    discipline: string;
    unitSystem: 'Metric' | 'Imperial';
    /** Title-block project number prefix for documents created from this template. */
    numberPrefix: string;
    levels: TemplateLevelSpec[];
    /** Phase names in construction sequence order. */
    phases: string[];
}

/** Unit presets per template unit system (BIM: project units defaults). */
export function unitSettingsFor(system: 'Metric' | 'Imperial'): UnitSettings {
    if (system === 'Imperial') {
        return {
            system,
            lengthUnit: 'Feet / Fractional Inches',
            areaUnit: 'Square Feet',
            volumeUnit: 'Cubic Yards',
            angleUnit: 'Degrees',
            lengthPrecision: 2,
        };
    }
    return {
        system: 'Metric',
        lengthUnit: 'Millimeters',
        areaUnit: 'Square Meters',
        volumeUnit: 'Cubic Meters',
        angleUnit: 'Decimal Degrees',
        lengthPrecision: 0,
    };
}

export const BUILTIN_PROJECT_TEMPLATES: ProjectTemplateSpec[] = [
    {
        id: 'tpl-arch-metric',
        name: 'Architectural Template (Metric)',
        description: 'Standard architectural template with story levels, floor plans, a 3D view and metric units. Default choice for building design.',
        discipline: 'Architecture',
        unitSystem: 'Metric',
        numberPrefix: '001',
        levels: [
            { name: 'Level 1', elevation: 0, isBuildingStory: true },
            { name: 'Level 2', elevation: 3000, isBuildingStory: true },
            { name: 'Roof', elevation: 6200, isBuildingStory: false },
        ],
        phases: ['New Construction'],
    },
    {
        id: 'tpl-struct-metric',
        name: 'Structural Template (Metric)',
        description: 'Structural template with a foundation level below grade, story levels and analytical-ready views for load-bearing design.',
        discipline: 'Structure',
        unitSystem: 'Metric',
        numberPrefix: '101',
        levels: [
            { name: 'Foundation', elevation: -1200, isBuildingStory: false },
            { name: 'Level 1', elevation: 0, isBuildingStory: true },
            { name: 'Level 2', elevation: 3000, isBuildingStory: true },
            { name: 'Roof', elevation: 6200, isBuildingStory: false },
        ],
        phases: ['New Construction'],
    },
    {
        id: 'tpl-mep-metric',
        name: 'Mechanical Template (Metric)',
        description: 'MEP template for HVAC, plumbing and electrical systems with story levels and service-oriented plan views.',
        discipline: 'Mechanical / MEP',
        unitSystem: 'Metric',
        numberPrefix: '201',
        levels: [
            { name: 'Level 1', elevation: 0, isBuildingStory: true },
            { name: 'Level 2', elevation: 3000, isBuildingStory: true },
            { name: 'Roof', elevation: 6200, isBuildingStory: false },
        ],
        phases: ['New Construction'],
    },
    {
        id: 'tpl-multi-metric',
        name: 'Multi-Discipline Template (Metric)',
        description: 'Combined template covering architecture, structure and systems in one document, including the below-grade foundation level.',
        discipline: 'Multi-Discipline',
        unitSystem: 'Metric',
        numberPrefix: '301',
        levels: [
            { name: 'Foundation', elevation: -1200, isBuildingStory: false },
            { name: 'Level 1', elevation: 0, isBuildingStory: true },
            { name: 'Level 2', elevation: 3000, isBuildingStory: true },
            { name: 'Roof', elevation: 6200, isBuildingStory: false },
        ],
        phases: ['New Construction'],
    },
    {
        id: 'tpl-construction-metric',
        name: 'Construction Template (Metric)',
        description: 'Construction-phase template pre-seeded with Existing / New Construction / Demolished phases for phasing-driven site work.',
        discipline: 'Construction',
        unitSystem: 'Metric',
        numberPrefix: '401',
        levels: [
            { name: 'Level 1', elevation: 0, isBuildingStory: true },
            { name: 'Level 2', elevation: 3000, isBuildingStory: true },
        ],
        phases: ['Existing', 'New Construction', 'Demolished'],
    },
];
